import React from "react";
import { useState } from "react";
import { CheckboxEditor } from "../../../../../../react-core/Html/CheckboxEditor";
import { Lightbox } from "../../../../../../react-core/Html/Components/Lightbox";
import { NoteType } from "../../../../../../Wrap/Constants/Enums/Customer/NoteType";
import { NoteDetailModel } from "../../../../../../Wrap/Models/Models/Customer/NoteDetailModel";
import { Table } from "../../../../../Shared/Table/Table";
import { DeleteLightbox } from "../../../../Common/DeleteLightbox";
import { SectionHeader } from "../../Common/SharedComponents";
import { AddNewButton } from "./AddNewButton";
import { UserClient } from "../../../../../../Wrap/Api/User/User";
import { IndividualUserFunctionClient } from "../../../../../../Wrap/Api/User/IndividualUserFunction";

interface IImportantFileProps {
    data: Array<NoteDetailModel>;
    onAdd: (data) => boolean;
    onSave: (data) => boolean;
    onDelete: (data) => boolean;
    hasReadWriteAccess: boolean;
}

const getTableConfig = (setNewData, setIsShow, setShowDeleteLightbox, setTextareaRequired, hasReadWriteAccess, hasImportantNotePermission) => {
    return [
        {
            title: "Note",
            key: "Note",
        },
        {
            title: "Critical flag",
            key: "NoteType",
            render: (data: NoteType) => {
                return data == NoteType.RegulatoryCritical ? "Yes" : "No";
            },
        },
        hasReadWriteAccess == true
            ? {
                  title: "Edit",
                  key: "",
                  tdCssWrapper: "cell-center",
                  thCssWrapper: "cell-center",
                  render: (data, row: NoteDetailModel) => {
                      return (
                          <button
                              className="edit-circle-icon-button label-sr-only"
                              title="Edit"
                              onClick={() => {
                                  if (hasImportantNotePermission){
                                    setNewData(row);
                                    setIsShow(true);
                                    setTextareaRequired(true);
                                  }
                              }}
                              disabled={!hasImportantNotePermission}
                          >
                              <span className="sr-only">Edit Notes</span>
                              <span className="icon" aria-hidden="true"></span>
                          </button>
                      );
                  },
              }
            : null,
        hasReadWriteAccess == true
            ? {
                  title: "Remove",
                  key: "",
                  tdCssWrapper: "cell-center",
                  thCssWrapper: "cell-center",
                  render: (data, row: NoteDetailModel) => {
                      return ( hasImportantNotePermission? (
                          <button
                              className="remove-button pl-0 pr-0 pb-35"
                              onClick={() => {
                                setNewData(row);
                                setShowDeleteLightbox(true);
                              }}
                          >
                              <span className="sr-only">Remove</span>
                              <span className="icon" aria-hidden="true"></span>
                          </button>) : null
                      );
                  },
              }
            : null,
    ];
};

export const ImportantFile = (props: IImportantFileProps) => {
    const [newData, setNewData] = useState<NoteDetailModel>({ NoteType: NoteType.RegulatoryCheck });
    const [isShow, setIsShow] = useState(false);
    const [showDeleteLightbox, setShowDeleteLightbox] = useState(false);
    const [textareaRequired, setTextareaRequired] = useState(false);
    const [hasImportantNotePermission, setHasImportantNotePermission] = useState(false);
    const [claccountid, setClaccountid] = useState(0);

    const client = new UserClient();
    const promise = client.get();
    promise.onSuccess((result) => {
        setClaccountid(result.ClientId)
        const FunctionName = "CanModifyImportantNotes"
        const functionCheckClient = new IndividualUserFunctionClient();
        const functionCheckPromise = functionCheckClient.get(claccountid, FunctionName);
        functionCheckPromise
            .onSuccess((data) => {
                setHasImportantNotePermission(data);
            })
            .onError(() => {
                setHasImportantNotePermission(false);
            });
    });
    promise.onError((err) => {}); 
    return (
        <>
            <div className="flex-space">
                {SectionHeader("Important notes")}
                {props?.hasReadWriteAccess == true && (
                    <div className="mb-20">
                        <AddNewButton
                            name="Add new important notes"
                            onClick={() => {
                                if (hasImportantNotePermission){
                                    setIsShow(true);
                                    setTextareaRequired(false);
                                }   
                            }}
                            isReview={!hasImportantNotePermission}
                        />
                    </div>
                )}
            </div>
            <Table
                data={props.data}
                col={getTableConfig(
                    setNewData,
                    setIsShow,
                    setShowDeleteLightbox,
                    setTextareaRequired,
                    props.hasReadWriteAccess,
                    hasImportantNotePermission
                )}
                showEmptyMsg
            />
            {isShow && (
                <Lightbox
                    cssWrapper="is-default-lightbox"
                    onClose={() => {
                        setIsShow(false);
                        setNewData({ NoteType: NoteType.RegulatoryCheck });
                    }}
                    show={isShow}
                    title={newData?.NoteId ? "Edit important notes" : "Add important notes"}
                >
                    <CheckboxEditor
                        value={newData?.NoteType == NoteType.RegulatoryCritical}
                        cssWrapper="control-size-60 square-checkbox"
                        label={"Critical flag"}
                        onChange={(v) => {
                            const _newData = JSON.parse(JSON.stringify(newData));
                            if (v) {
                                _newData.NoteType = NoteType.RegulatoryCritical;
                            } else {
                                _newData.NoteType = NoteType.RegulatoryCheck;
                            }
                            setNewData(_newData);
                        }}
                    />
                    <div className="controls-textarea mb-30">
                        <textarea
                            rows={5}
                            onChange={(e) => {
                                const _newData = JSON.parse(JSON.stringify(newData));
                                _newData.Note = e.target.value;
                                setNewData(_newData);
                                if (e.target.value.trim() !== "") {
                                    setTextareaRequired(true);
                                } else {
                                    setTextareaRequired(false);
                                }
                            }}
                            maxLength={500}
                            value={newData?.Note}
                            placeholder={"Important note is mandatory and has a character limit of 500"}
                            required={true}
                        ></textarea>
                    </div>

                    <div className="row">
                        <div className="col-auto">
                            <button
                                className="btn secondary"
                                onClick={() => {
                                    setNewData({ NoteType: NoteType.RegulatoryCheck });
                                    setIsShow(false);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                        <div className="col-auto">
                            <button
                                className="btn primary"
                                disabled={!textareaRequired}
                                onClick={() => {
                                    if (newData?.NoteId) {
                                        if (props.onSave(newData)) {
                                            setNewData({ NoteType: NoteType.RegulatoryCheck });
                                            setIsShow(false);
                                        }
                                    } else {
                                        if (props.onAdd(newData)) {
                                            setNewData({ NoteType: NoteType.RegulatoryCheck });
                                            setIsShow(false);
                                        }
                                    }
                                }}
                            >
                                {newData?.NoteId ? "Save changes" : "Add"}
                            </button>
                        </div>
                    </div>
                </Lightbox>
            )}
            {showDeleteLightbox && (
                <DeleteLightbox
                    onClose={() => {
                        setNewData({ NoteType: NoteType.RegulatoryCheck });
                        setShowDeleteLightbox(false);
                    }}
                    onDelete={() => {
                        if (props.onDelete(newData)) {
                            setNewData({ NoteType: NoteType.RegulatoryCheck });
                            setShowDeleteLightbox(false);
                        }
                    }}
                    content={
                        <>
                            <p>Are you sure?</p>
                        </>
                    }
                />
            )}
        </>
    );
};
